import React, { useMemo, useState, useEffect, useTransition, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  Stack,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  useTheme,
  useMediaQuery,
  Autocomplete,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  WarningAmber as WarningIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import axios from '../../utils/axiosConfig';
import { formatShootingSchedule, formatPagesInEighths } from '../../utils/scheduleFormatter';
import { TAG_TYPES } from '../SimpleScreenplayViewer';
import * as shootingScheduleService from '../../services/shootingScheduleService';
import { getHierarchyStyle, hasHierarchy } from '../../utils/castStyles';

// Memoized camera equipment autocomplete to reduce re-renders per scene row
const CameraEquipAutocomplete = React.memo(function CameraEquipAutocomplete({
  open,
  sid,
  equipList,
  options,
  onChange,
  onBlur,
  placeholder = 'Select or type camera gear',
  textFieldSx,
}) {
  if (!open) return null;
  return (
    <Autocomplete
      multiple
      freeSolo
      // Open only after typing, not on focus
      // openOnFocus
      disablePortal
      isOptionEqualToValue={(option, value) => option === value}
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderTags={() => null}
      clearOnBlur={false}
      selectOnFocus
      filterSelectedOptions
      autoHighlight
      includeInputInList={false}
      filterOptions={(optionsArg, state) => {
        const q = (state.inputValue || '').toLowerCase();
        if (!q || q.length < 2) return [];
        const filtered = optionsArg.filter(o => (o || '').toLowerCase().includes(q));
        return filtered.slice(0, 50);
      }}
      noOptionsText="Type to search camera gear"
      loadingText="Searching..."
      options={options}
      value={equipList}
      onChange={(e, list) => onChange(list)}
      onBlur={onBlur}
      renderInput={(params) => (
        <TextField {...params} size="small" placeholder={placeholder} sx={textFieldSx} />
      )}
      sx={{ mt: 0.75, minWidth: 260 }}
    />
  );
});

// Memoized Autocomplete for category manual tags (non-camera)
const CategoryTagAutocomplete = React.memo(function CategoryTagAutocomplete({
  open,
  options,
  value,
  placeholder,
  onChange,
  onBlur,
  minWidth = 220,
  textFieldSx,
}) {
  if (!open) return null;
  return (
    <Autocomplete
      multiple
      freeSolo
      disablePortal
      isOptionEqualToValue={(option, val) => option === val}
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderTags={() => null}
      clearOnBlur={false}
      selectOnFocus
      filterSelectedOptions
      autoHighlight
      includeInputInList={false}
      filterOptions={(opts, state) => {
        const q = (state.inputValue || '').toLowerCase();
        if (!q || q.length < 2) return [];
        const filtered = (opts || []).filter(o => (String(o || '')).toLowerCase().includes(q));
        return filtered.slice(0, 50);
      }}
      noOptionsText="Type to search"
      loadingText="Searching..."
      options={options}
      value={value}
      onChange={(e, list) => onChange?.(list)}
      onBlur={onBlur}
      renderInput={(params) => (
        <TextField {...params} size="small" placeholder={placeholder || 'Select or type'} sx={textFieldSx} />
      )}
      sx={{ mt: 0.75, minWidth }}
    />
  );
});

// MVP: Accept scenes and breaks via props when available
// For now, we keep local state empty and show a friendly placeholder
function ShootingScheduleTab({ projectId, scenes = [], breaks = [], sceneBreakdown = {}, onUpdateSpecialEquipment, projectMeta }) {
  // Debug flag for this module
  const DEBUG_SCHEDULE_TAB = false;
  const dbg = (...args) => { if (DEBUG_SCHEDULE_TAB) console.debug(...args); };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const theme = useTheme();
  
  // Consistent text field styling
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      fontWeight: 600
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.3)
        : alpha(theme.palette.grey[50], 0.5),
      borderRadius: 1,
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderWidth: 1.5
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.4)
          : alpha(theme.palette.grey[50], 0.8),
        '& fieldset': {
          borderColor: theme.palette.primary.main
        }
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.primary.main, 0.02),
        '& fieldset': {
          borderWidth: 2
        }
      }
    }
  };
  const [isOpening, startTransition] = useTransition();
  // Local special equipment per sceneId (array of strings)
  const [specialEquipMap, setSpecialEquipMap] = useState({});
  const [equipDraftMap, setEquipDraftMap] = useState({});
  // Per-scene minutes overrides and editing state
  const [minutesOverride, setMinutesOverride] = useState({}); // { [sceneId]: number }
  const [editingMinutesFor, setEditingMinutesFor] = useState(null); // sceneId
  const [minutesDraft, setMinutesDraft] = useState('');
  // Project cast (for hierarchy and normalization like OneLiner)
  const [castMembers, setCastMembers] = useState([]);
  const MAX_ITEM_LEN = 120;
  // Per-scene UI toggle: only one camera editor open at a time to reduce re-renders
  const [equipEditorOpenSid, setEquipEditorOpenSid] = useState(null); // sceneId or null
  // Inline non-blocking custom input for Camera
  const [cameraCustomSid, setCameraCustomSid] = useState(null);
  const [cameraCustomDraft, setCameraCustomDraft] = useState('');
  // Inline non-blocking custom input for categories
  const [catCustomOpen, setCatCustomOpen] = useState({}); // { [sid]: { [catKey]: true } }
  const [catCustomDraft, setCatCustomDraft] = useState({}); // { [sid]: { [catKey]: string } }
  // Per-scene, per-category UI toggles and manual entries (for non-camera categories)
  const [catEditorOpen, setCatEditorOpen] = useState({}); // { [sceneId]: { [catKey]: boolean } }
  const [catManualMap, setCatManualMap] = useState({}); // { [sceneId]: { [catKey]: string[] } }
  // Per-scene overrides for auto (breakdown-sourced) tags: remove/rename without mutating Breakdown
  const [catOverrideMap, setCatOverrideMap] = useState({}); // { [sceneId]: { [catKey]: { remove: string[], rename: { [old]: new } } } }
  // Normalize scene numbers for reliable fallback matching
  const normalizeSceneNumber = (n) => {
    if (n == null) return '';
    let raw = String(n).toUpperCase().trim();
    // Strip common prefixes like SCENE, SC., SC
    raw = raw.replace(/^SC(?:ENE)?\.?\s*/i, '');
    // Remove non-alphanumeric separators (keep A-Z and 0-9)
    raw = raw.replace(/[^A-Z0-9]/gi, '');
  return raw;
};

  // Memoized row wrapper that renders either a scene or a break strip
  const SceneStripRow = React.memo(
    ({ strip, idx, minutesOverride, catManualMap, specialEquipMap, renderSceneStrip, renderBreakStrip }) => {
      return strip?.type === 'SCENE' ? renderSceneStrip(strip, idx) : renderBreakStrip(strip, idx);
    },
    (prev, next) => {
      const a = prev.strip || {};
      const b = next.strip || {};
      if (a.type !== b.type) return false;
      if (prev.idx !== next.idx) return false;
      // For breaks, allow re-render only if label/type/time changed
      if (a.type !== 'SCENE') {
        return a.label === b.label && a.minutes === b.minutes && a.breakType === b.breakType;
      }
      const sidA = a.sceneId || a.id || a._id;
      const sidB = b.sceneId || b.id || b._id;
      if (sidA !== sidB) return false;
      // Compare key scene display fields
      const fields = ['number','normalizedNumber','intExt','effectiveDayNight','pages','estimatedMinutes','location','set','description','storyDay'];
      for (const f of fields) {
        if ((a?.[f] ?? null) !== (b?.[f] ?? null)) return false;
      }
      // Per-scene minutes override
      const moA = (prev.minutesOverride || {})[sidA];
      const moB = (next.minutesOverride || {})[sidB];
      if ((moA ?? null) !== (moB ?? null)) return false;
      // Shallow compare special equipment arrays
      const seA = (prev.specialEquipMap || {})[sidA] || [];
      const seB = (next.specialEquipMap || {})[sidB] || [];
      if (seA.length !== seB.length) return false;
      for (let i = 0; i < seA.length; i++) if (seA[i] !== seB[i]) return false;
      // Shallow compare manual category map for this scene (keys and joined values)
      const cmA = (prev.catManualMap || {})[sidA] || {};
      const cmB = (next.catManualMap || {})[sidB] || {};
      const keysA = Object.keys(cmA).sort();
      const keysB = Object.keys(cmB).sort();
      if (keysA.length !== keysB.length) return false;
      for (let i = 0; i < keysA.length; i++) if (keysA[i] !== keysB[i]) return false;
      for (const k of keysA) {
        const arrA = Array.isArray(cmA[k]) ? cmA[k] : [];
        const arrB = Array.isArray(cmB[k]) ? cmB[k] : [];
        if (arrA.length !== arrB.length) return false;
        for (let i = 0; i < arrA.length; i++) if (arrA[i] !== arrB[i]) return false;
      }
      // Ignore function prop identity; if we reached here consider equal
      return true;
    }
  );

  // UI flag: whether to render chips for selected category items in scene strips
  const SHOW_CATEGORY_CHIPS = false;

  // Lightweight virtualization: renders only visible items with spacers
  const VirtualList = ({ items = [], itemHeight = 104, overscan = 6, renderItem, sx }) => {
    const containerRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportH, setViewportH] = useState(560);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const update = () => setViewportH(el.clientHeight || 560);
      update();
      let ro;
      try {
        ro = new ResizeObserver(update);
        ro.observe(el);
      } catch {}
      return () => { try { ro && ro.disconnect(); } catch {} };
    }, []);

    const onScroll = (e) => setScrollTop(e.currentTarget.scrollTop || 0);
    const total = items.length;
    const visibleCount = Math.max(1, Math.ceil(viewportH / itemHeight));
    let start = Math.floor(scrollTop / itemHeight) - overscan;
    if (start < 0) start = 0;
    let end = start + visibleCount + overscan * 2;
    if (end > total) end = total;
    const topPad = start * itemHeight;
    const bottomPad = (total - end) * itemHeight;

    return (
      <Box ref={containerRef} onScroll={onScroll} sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', ...sx }}>
        <Box style={{ height: topPad }} />
        {items.slice(start, end).map((item, i) => renderItem(item, start + i))}
        <Box style={{ height: bottomPad }} />
      </Box>
    );
  };

  // Build printable Shooting Schedule (professional layout)
  const handlePrint = () => {
    try {
      const byDay = (() => {
        const out = [];
        const all = Array.isArray(scenes) ? scenes : [];
        const dayBreaks = (breaks || []).filter(b => (b?.type || '').toUpperCase() === 'DAY_BREAK').sort((a,b)=> (a.afterSceneIndex||0)-(b.afterSceneIndex||0));
        let start = 0;
        for (const br of dayBreaks) {
          const end = Math.min(Math.max(0, br.afterSceneIndex || 0), all.length - 1);
          if (end >= start) out.push({ start, end, scenes: all.slice(start, end + 1) });
          start = end + 1;
        }
        if (start <= all.length - 1) out.push({ start, end: all.length - 1, scenes: all.slice(start) });
        if (!out.length) out.push({ start: 0, end: all.length - 1, scenes: all });
        return out;
      })();

      const getNum = (s) => s?.sceneNumber?.standard ?? s?.sceneNumber ?? s?.number ?? '';
      const getIE = (s) => (s?.location_type || s?.intExt || '').toString().toUpperCase();
      const getLoc = (s) => {
        const loc = s?.location || '';
        const area = s?.area ? ` - ${s.area}` : '';
        return `${loc}${area}`.trim();
      };
      const getToD = (s) => (s?.time || s?.effectiveDayNight || s?.dayNight || '').toString().toUpperCase();
      const getPages = (s) => s?.pageLengthEighths != null ? formatPagesInEighths(Number(s.pageLengthEighths) || 0) : (s?.pages || '');
      const getEst = (s) => {
        const sid = s?.id || s?._id || s?.sceneId;
        const ov = sid ? minutesOverride?.[sid] : null;
        const base = s?.estimatedMinutes;
        const v = Number(ov != null ? ov : base);
        return Number.isFinite(v) && v > 0 ? `${v} min` : '';
      };

      const styles = `
        <style>
          @page { size: Letter; margin: 0.6in; }
          @media print {
            html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
          body { font-family: Roboto, Arial, sans-serif; color: #111; }
          .sheet { page-break-after: always; }
          .banner { padding: 14px 16px; margin: 0 0 10px 0; position: relative; border-radius: 10px; 
            background: linear-gradient(to right bottom, rgba(33,150,243,0.12), rgba(255,255,255,0.9));
            border: 1px solid rgba(33,150,243,0.12);
          }
          .banner::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg, #2196F3, #1976D2); border-top-left-radius: 10px; border-top-right-radius: 10px; }
          .title { font-size: 16px; font-weight: 700; margin: 0 0 2px 0; }
          .subtitle { font-size: 12px; color: #555; margin: 0; }
          .table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          .thead th { text-transform: uppercase; letter-spacing: .6px; font-size: 10px; text-align: left; background: #000; color:#fff; padding: 6px 8px; }
          .tbody td { font-size: 11px; padding: 7px 8px; border-bottom: 1px solid #e0e0e0; vertical-align: top; }
          .c-scene { width: 58px; text-align: center; }
          .c-ie { width: 52px; text-align: center; }
          .c-desc { width: auto; }
          .c-tod { width: 70px; text-align: center; }
          .c-pages { width: 64px; text-align: center; }
          .c-est { width: 60px; text-align: center; }
          .day { margin-bottom: 26px; }
          .dept-row td { font-size: 10px; color: #444; background: #fafafa; border-bottom: 1px solid #eee; }
          .dept { display: inline-block; margin-right: 14px; }
          .dept b { text-transform: uppercase; font-weight: 700; font-size: 9px; color: #666; margin-right: 4px; }
        </style>
      `;

      const htmlDays = byDay.map((bucket, di) => {
        const head = `Day ${di + 1}${projectMeta?.shootingDayDates?.[di] ? ' — ' + projectMeta.shootingDayDates[di] : ''}`;
        const { start: dayStart, end: dayEnd, scenes: dayScenes } = bucket;
        // Build lunch map keyed by afterSceneIndex within this day's range
        const lunchList = (breaks || [])
          .filter(b => (b?.type || '').toUpperCase() === 'LUNCH')
          .filter(b => typeof b.afterSceneIndex === 'number' && b.afterSceneIndex >= dayStart && b.afterSceneIndex <= dayEnd)
          .sort((a,b) => a.afterSceneIndex - b.afterSceneIndex);
        const lunchByIdx = new Map();
        lunchList.forEach(b => {
          const arr = lunchByIdx.get(b.afterSceneIndex) || [];
          arr.push(b);
          lunchByIdx.set(b.afterSceneIndex, arr);
        });

        const rows = dayScenes.map((s, localIdx) => {
          const globalIdx = dayStart + localIdx;
          const scene = escapeHtml(String(getNum(s) || ''));
          const ie = escapeHtml(String(getIE(s) || ''));
          const desc = escapeHtml(String(getLoc(s) || s?.description || ''));
          const tod = escapeHtml(String(getToD(s) || ''));
          const pages = escapeHtml(String(getPages(s) || ''));
          const est = escapeHtml(String(getEst(s) || ''));
          // Department items (from breakdown + manual + special equipment)
          const sid = s?.id || s?._id || s?.sceneId || '';
          const bd = sceneBreakdown?.[String(sid)] || {};
          const groupedRaw = DISABLE_AUTO_POPULATE ? {} : (bd.grouped || {});
          const grouped = applyOverrides(String(sid), groupedRaw);
          const toNames = (arr) => (Array.isArray(arr) ? arr : []).map(t => t?.name || t?.text || t?.title || '').filter(Boolean);
          const manualFor = (key) => toNames((catManualMap?.[String(sid)] || {})[key]);
          const merge = (a1, a2) => Array.from(new Set([...(a1||[]), ...(a2||[])])).slice(0, 20);
          const cam = Array.isArray(specialEquipMap?.[String(sid)]) ? specialEquipMap[String(sid)] : [];
          const props = merge(toNames(grouped.PROPS), manualFor('PROPS'));
          const light = merge(toNames(grouped.LIGHTING), manualFor('LIGHTING'));
          const veh = merge(toNames(grouped.VEHICLES), manualFor('VEHICLES'));
          const sound = merge(toNames(grouped.SOUND), manualFor('SOUND'));
          const vfx = merge(toNames(grouped.VFX || grouped.VISUAL_EFFECTS), manualFor('VFX'));
          const sfx = merge(toNames(grouped.SPECIAL_EFFECTS), manualFor('SPECIAL_EFFECTS'));
          const stunts = merge(toNames(grouped.STUNTS), manualFor('STUNTS'));
          const animals = merge(toNames(grouped.ANIMALS), manualFor('ANIMALS'));
          const hair = toNames(grouped.HAIR);
          const makeup = toNames(grouped.MAKEUP);
          const hairMakeup = merge([...hair, ...makeup], manualFor('HAIR_MAKEUP'));
          const misc = manualFor('MISC');
          const asLine = (label, list) => list && list.length ? `<span class="dept"><b>${label}:</b> ${escapeHtml(list.join(', '))}</span>` : '';
          const deptHtml = [
            asLine('Camera', cam),
            asLine('Props', props),
            asLine('Lighting', light),
            asLine('Vehicles', veh),
            asLine('Sound', sound),
            asLine('VFX', vfx),
            asLine('Special FX', sfx),
            asLine('Stunts', stunts),
            asLine('Animals', animals),
            asLine('Hair/Makeup', hairMakeup),
            asLine('Misc', misc),
          ].filter(Boolean).join(' ');

          let html = `<tr>
            <td class="c-scene">${scene}</td>
            <td class="c-ie">${ie}</td>
            <td class="c-desc">${desc}</td>
            <td class="c-tod">${tod}</td>
            <td class="c-pages">${pages}</td>
            <td class="c-est">${est}</td>
          </tr>
          <tr class="dept-row"><td colspan="6">${deptHtml}</td></tr>`;
          // Inject lunch rows that occur after this scene index
          const lunches = lunchByIdx.get(globalIdx) || [];
          lunches.forEach(l => {
            const label = l.label || 'Lunch';
            const dur = typeof l.durationMinutes === 'number' ? ` — ${l.durationMinutes} min` : '';
            html += `<tr class="break-row"><td colspan="6"><b>Lunch:</b> ${escapeHtml(label)}${dur}</td></tr>`;
          });
          return html;
        }).join('');

        return `
          <section class="sheet">
            <div class="banner">
              <div class="title">${escapeHtml(projectMeta?.title || 'Shooting Schedule')}</div>
              <div class="subtitle">${escapeHtml(head)}</div>
            </div>
            <table class="table">
              <thead class="thead">
                <tr>
                  <th class="c-scene">Scene</th>
                  <th class="c-ie">I/E</th>
                  <th class="c-desc">Description</th>
                  <th class="c-tod">ToD</th>
                  <th class="c-pages">Pages</th>
                  <th class="c-est">Est</th>
                </tr>
              </thead>
              <tbody class="tbody">${rows}</tbody>
            </table>
          </section>
        `;
      }).join('');

      const docHtml = `<!DOCTYPE html><html><head><meta charset="utf-8">${styles}</head><body>${htmlDays}</body></html>`;
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      const iDoc = iframe.contentWindow || iframe.contentDocument;
      const doc = iDoc.document || iDoc;
      doc.open();
      doc.write(docHtml);
      doc.close();
      setTimeout(() => {
        iDoc.focus();
        iDoc.print();
        setTimeout(() => { document.body.removeChild(iframe); }, 500);
      }, 300);
    } catch (e) {
      console.error('Print schedule failed', e);
    }
  };

  // Fast scene lookups by id and normalized number to avoid O(N) scans per row
  const scenesById = useMemo(() => {
    const map = new Map();
    (scenes || []).forEach((s) => {
      const id = s.id || s._id || s.sceneId;
      if (id != null) map.set(String(id), s);
    });
    return map;
  }, [scenes]);

  const scenesByNormNum = useMemo(() => {
    const map = new Map();
    (scenes || []).forEach((s) => {
      const num = normalizeSceneNumber(s.sceneNumber?.standard || s.sceneNumber || s.number || '');
      if (num) map.set(num, s);
    });
    return map;
  }, [scenes]);

  // Predefined camera/special equipment options for quick selection
  const SPECIAL_EQUIP_OPTIONS = [
    'Steadicam / gimbal rigs (Ronin, Movi, etc.)',
    'Drone (with licensed operator)',
    'Jib / crane / Technocrane',
    'Dolly & track',
    'Car mount / process trailer / insert car',
    'Underwater housing / splash bag',
    'High-speed camera (Phantom, etc.)',
    '3D rig / VR camera setup',
    'Remote head / stabilized head',
  ];
  const CUSTOM_OPTION = 'Custom...';
  const ALL_EQUIP_OPTIONS = useMemo(() => [CUSTOM_OPTION, ...SPECIAL_EQUIP_OPTIONS], []);

  // Extra suggestion options per category (beyond breakdown-derived)
  const EXTRA_CATEGORY_OPTIONS = {
    LIGHTING: [
      'Condors / lifts for lights',
      'Generators (portable or truck-mounted)',
      'Large diffusion frames / silks',
      'Fog / haze machines',
      'Flame bars / fire effects lighting rigs',
      'Rigging for complex setups (truss, pipe grids)',
      'Lightning machines or strobe rigs',
    ],
    SOUND: [
      'Wireless mic kits (for large cast scenes)',
      'Multi-track field recorders',
      'Long-range shotgun mics / parabolic mics',
      'ADR setup on location (rare, but sometimes)',
      'Playback',
    ],
    VEHICLES: [
      'Process trailer (for driving shots)',
      'Camera car / pursuit vehicle',
      'Drones (for aerials)',
      'Boats / watercraft rigs',
      'Helicopter or aircraft rigs',
    ],
    VFX: [
      'Green screen / blue screen setups',
      'Tracking markers & rigs',
      'Motion capture suits / volume setup',
      'LED volume wall (virtual production)',
    ],
    SPECIAL_EFFECTS: [
      'Squibs / blood rigs',
      'Breakaway props / furniture',
      'Fire rigs (torches, flame bars, propane effects)',
      'Rain towers / water rigs',
      'Smoke / fog / haze machines',
      'Wind machines (giant fans)',
      'Dust / debris blowers',
      'Controlled explosions / pyrotechnics',
    ],
    STUNTS: [
      'Stunt mats / crash pads',
      'Wire rigs / harnesses',
      'Air rams / ratchets',
      'Safety nets / scaffolding',
      'Fire safety gear (blankets, extinguishers, gel)',
      'Medics / safety officers',
    ],
    ANIMALS: [],
    HAIR_MAKEUP: [],
    MISC: [],
  };

  // Memoized options for MISC category (stable reference)
  const MISC_OPTIONS = useMemo(() => [CUSTOM_OPTION, ...EXTRA_CATEGORY_OPTIONS.MISC], []);

  // Stable base options map for all categories to avoid recreating arrays each render
  const BASE_CAT_OPTIONS = useMemo(() => ({
    LIGHTING: [CUSTOM_OPTION, ...EXTRA_CATEGORY_OPTIONS.LIGHTING],
    PROPS: [CUSTOM_OPTION, ...((EXTRA_CATEGORY_OPTIONS.PROPS) || [])],
    SOUND: [CUSTOM_OPTION, ...EXTRA_CATEGORY_OPTIONS.SOUND],
    VFX: [CUSTOM_OPTION, ...EXTRA_CATEGORY_OPTIONS.VFX],
    SPECIAL_EFFECTS: [CUSTOM_OPTION, ...EXTRA_CATEGORY_OPTIONS.SPECIAL_EFFECTS],
    VEHICLES: [CUSTOM_OPTION, ...EXTRA_CATEGORY_OPTIONS.VEHICLES],
    STUNTS: [CUSTOM_OPTION, ...EXTRA_CATEGORY_OPTIONS.STUNTS],
    ANIMALS: [CUSTOM_OPTION, ...EXTRA_CATEGORY_OPTIONS.ANIMALS],
    HAIR_MAKEUP: [CUSTOM_OPTION, ...EXTRA_CATEGORY_OPTIONS.HAIR_MAKEUP],
    MISC: MISC_OPTIONS,
  }), []);

  // Feature flag: disable auto-populated breakdown items (show only manual entries and special equipment)
  const DISABLE_AUTO_POPULATE = false;

  // Stable style objects to prevent re-renders
  const STABLE_STYLES = useMemo(() => ({
    sceneStripGrid: {
      display: 'grid',
      gridTemplateColumns: '84px 68px minmax(240px, 1fr) 92px minmax(80px, max-content) 68px 80px',
      alignItems: 'center',
      columnGap: 0.9,
      py: 1.25,
      px: 1,
      borderRadius: 1,
      border: '1px solid',
      mb: 1,
    },
    categoryBox: {
      pl: { xs: 0, md: 1.5 },
      borderLeft: { xs: 'none', md: (t) => `1px solid ${alpha(t.palette.divider, 0.4)}` },
    },
    addButton: {
      minWidth: 'auto',
      width: 24,
      height: 24,
      p: 0,
    },
    chipSxBase: {
      borderRadius: 999,
      '& .MuiChip-label': { px: 0.75, fontWeight: 500 },
    },
    textSummary: {
      color: '#888',
      mt: 0.5,
    },
  }), []);

  const sanitize = (s) => (s || '').trim();
  const tooLong = (s) => s.length > MAX_ITEM_LEN;
  const makeSet = (arr) => new Set((arr || []).map(v => (v || '').toLowerCase()));
  const escapeHtml = (str) => String(str || '').replace(/[&<>"']/g, (ch) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;'}[ch]));

  // Centralized tag columns (after Camera) used across header, rows, and CSV
  // Order below Camera: Lighting, Props, Sound, VFX, Special FX, Vehicles, Stunts
  const SCHEDULE_TAG_COLUMNS = [
    { label: 'Lighting', keys: ['LIGHTING'], key: 'LIGHTING' },
    { label: 'Props', keys: ['PROPS'], key: 'PROPS' },
    { label: 'Sound', keys: ['SOUND'], key: 'SOUND' },
    { label: 'VFX', keys: ['VFX', 'VISUAL_EFFECTS'], key: 'VFX' },
    { label: 'Special FX', keys: ['SPECIAL_EFFECTS'], key: 'SPECIAL_EFFECTS' },
    { label: 'Vehicles', keys: ['VEHICLES'], key: 'VEHICLES' },
    { label: 'Stunts', keys: ['STUNTS'], key: 'STUNTS' },
    { label: 'Animals', keys: ['ANIMALS'], key: 'ANIMALS' },
    { label: 'Hair/Makeup', keys: ['HAIR', 'MAKEUP'], key: 'HAIR_MAKEUP' },
  ];

  // Category color accents for chips
  const CATEGORY_STYLES = {
    LIGHTING: { bg: 'rgba(255, 193, 7, 0.16)', bd: 'rgba(255, 193, 7, 0.45)', fg: '#FFFFFF' }, // amber
    PROPS: { bg: 'rgba(33, 150, 243, 0.16)', bd: 'rgba(33, 150, 243, 0.45)', fg: '#FFFFFF' }, // blue
    SOUND: { bg: 'rgba(0, 150, 136, 0.16)', bd: 'rgba(0, 150, 136, 0.45)', fg: '#FFFFFF' }, // teal
    VFX: { bg: 'rgba(156, 39, 176, 0.16)', bd: 'rgba(156, 39, 176, 0.45)', fg: '#FFFFFF' }, // purple
    SPECIAL_EFFECTS: { bg: 'rgba(244, 67, 54, 0.16)', bd: 'rgba(244, 67, 54, 0.45)', fg: '#FFFFFF' }, // red
    VEHICLES: { bg: 'rgba(63, 81, 181, 0.16)', bd: 'rgba(63, 81, 181, 0.45)', fg: '#FFFFFF' }, // indigo
    STUNTS: { bg: 'rgba(255, 87, 34, 0.16)', bd: 'rgba(255, 87, 34, 0.45)', fg: '#FFFFFF' }, // deep orange
    ANIMALS: { bg: 'rgba(121, 134, 203, 0.16)', bd: 'rgba(121, 134, 203, 0.45)', fg: '#FFFFFF' }, // blue-grey
    HAIR_MAKEUP: { bg: 'rgba(255, 214, 0, 0.16)', bd: 'rgba(255, 214, 0, 0.45)', fg: '#FFFFFF' }, // amber/yellow
    MISC: { bg: 'rgba(158, 158, 158, 0.16)', bd: 'rgba(158, 158, 158, 0.45)', fg: '#FFFFFF' }, // grey
  };

  // Helper: get plain names for one or more category keys from grouped tags
  const getNamesFromGrouped = (grouped, keys) => {
    const arrs = (keys || []).map(k => Array.isArray(grouped?.[k]) ? grouped[k] : []);
    return arrs.flat().map(t => t?.name || t?.text || t?.title || '').filter(Boolean);
  };

  // Small renderer for limited chips + tooltip; supports item objects with source and deletable
  // items: array of either strings or { name, source: 'breakdown'|'manual', deletable?: boolean }
  const TagChipList = React.memo(function TagChipList({ names, items, idPrefix, chipSx, onDelete, onAutoRemove }) {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const [ctxAnchor, setCtxAnchor] = React.useState(null);
    const [ctxName, setCtxName] = React.useState(null);
    let list = [];
    if (Array.isArray(items)) {
      list = items.map(it => typeof it === 'string' ? ({ name: it, source: 'manual', deletable: !!onDelete }) : it)
        .filter(it => it && it.name);
    } else if (Array.isArray(names)) {
      list = names.filter(Boolean).map(n => ({ name: n, source: 'manual', deletable: !!onDelete }));
    }
    if (!list.length) return null;
    const displayLimit = isXs ? 2 : 3;
    const display = list.slice(0, displayLimit);
    const more = list.length - displayLimit;
    const fullTooltip = list.map(it => `${it.name}${it.source ? ` (${it.source})` : ''}`).join(', ');
    const renderChip = (it, i) => {
      const isManual = it.source === 'manual';
      const sx = {
        ...chipSx,
        backgroundColor: isManual ? chipSx?.backgroundColor : 'transparent',
        borderColor: isManual ? chipSx?.borderColor : (t) => t.palette.divider,
        color: isManual ? (chipSx?.color || '#fff') : ((t) => t.palette.text.primary),
        borderWidth: 1,
        borderStyle: 'solid',
        px: 1,
        py: 0,
        borderRadius: 999,
        fontSize: '0.75rem',
        '& .MuiChip-deleteIcon': { color: 'inherit' },
      };
      return (
        <Tooltip key={`${idPrefix}_${i}`} title={it.source ? `Source: ${it.source}` : ''} arrow>
          <Chip
            size="small"
            variant={isManual ? 'filled' : 'outlined'}
            label={it.name}
            sx={sx}
            onDelete={isManual && onDelete ? () => onDelete(it.name) : undefined}
            onContextMenu={(e) => {
              if (isManual) return; // only auto chips get context menu
              e.preventDefault();
              setCtxAnchor(e.currentTarget);
              setCtxName(it.name);
            }}
          />
        </Tooltip>
      );
    };
    return (
      <>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          {display.map(renderChip)}
          {more > 0 ? (
            <Tooltip title={fullTooltip} arrow>
              <Chip size="small" variant="outlined" label={`+${more} more`} sx={{ ...chipSx, backgroundColor: 'transparent' }} />
            </Tooltip>
          ) : null}
        </Stack>
        <Menu
          anchorEl={ctxAnchor}
          open={Boolean(ctxAnchor)}
          onClose={() => { setCtxAnchor(null); setCtxName(null); }}
        >
          <MenuItem onClick={() => {
            if (onAutoRemove && ctxName) onAutoRemove(ctxName);
            setCtxAnchor(null); setCtxName(null);
          }}>Remove from schedule</MenuItem>
        </Menu>
      </>
    );
  });

  // Load project cast similar to OneLiner for consistent casting and hierarchy
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!projectId) return;
        // Authorization header is typically set by axiosConfig, but include fallback
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/v1/cast/project/${projectId}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
        if (!cancelled && Array.isArray(res.data)) setCastMembers(res.data);
      } catch (e) {
        // Non-fatal; cast chips will fall back to plain names
        console.warn('Schedule: failed to load project cast', e);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // getHierarchyStyle/hasHierarchy now imported from utils/castStyles for consistency

  // Map INT/EXT and DAY/NIGHT to subtle background and solid border colors (mirrors OneLiner styling)
  const getStripBgColor = (strip, theme) => {
    const isDark = theme.palette.mode === 'dark';
    const intExt = (strip.intExt || '').toUpperCase();
    const tod = (strip.effectiveDayNight || strip.dayNight || '').toUpperCase();

    const rgba = (r, g, b, a) => `rgba(${r}, ${g}, ${b}, ${a})`;

    // INT only
    if (intExt.includes('INT') && !intExt.includes('EXT')) {
      if (tod.includes('DAY')) return isDark ? rgba(255, 255, 255, 0.07) : rgba(255, 255, 255, 0.04);
      if (tod.includes('NIGHT')) return isDark ? rgba(33, 150, 243, 0.15) : rgba(33, 150, 243, 0.10); // blue
      if (tod.includes('MORNING')) return isDark ? rgba(0, 188, 212, 0.07) : rgba(0, 188, 212, 0.04);
      if (tod.includes('EVENING')) return isDark ? rgba(96, 125, 139, 0.07) : rgba(96, 125, 139, 0.04);
    }

    // EXT only
    if (intExt.includes('EXT') && !intExt.includes('INT')) {
      if (tod.includes('DAY')) return isDark ? rgba(255, 235, 59, 0.15) : rgba(255, 235, 59, 0.10); // yellow
      if (tod.includes('NIGHT')) return isDark ? rgba(103, 58, 183, 0.15) : rgba(103, 58, 183, 0.10); // purple
      if (tod.includes('DAWN') || tod.includes('SUNRISE')) return isDark ? rgba(255, 152, 0, 0.07) : rgba(255, 152, 0, 0.04);
      if (tod.includes('DUSK') || tod.includes('SUNSET')) return isDark ? rgba(244, 67, 54, 0.07) : rgba(244, 67, 54, 0.04);
      if (tod.includes('MORNING')) return isDark ? rgba(139, 195, 74, 0.07) : rgba(139, 195, 74, 0.04);
    }

    // INT/EXT combined
    if (intExt.includes('I/E') || intExt.includes('INT/EXT')) {
      if (tod.includes('DAY')) return isDark ? rgba(0, 150, 136, 0.07) : rgba(0, 150, 136, 0.04);
      if (tod.includes('NIGHT')) return isDark ? rgba(103, 58, 183, 0.07) : rgba(103, 58, 183, 0.04);
    }

    // CONT, CONT., LATER
    if (tod.includes('CONT') || tod === 'CONT.') return isDark ? rgba(158, 158, 158, 0.07) : rgba(158, 158, 158, 0.04);

    // Default subtle grey
    return isDark ? rgba(97, 97, 97, 0.07) : rgba(97, 97, 97, 0.04);
  };

  const renderColumnHeader = () => {
    return (
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '84px 68px minmax(240px, 1fr) 92px minmax(80px, max-content) 68px 80px',
        alignItems: 'center',
        columnGap: 0.9,
        py: 0.75,
        px: 1,
        mb: 0.5,
        backgroundColor: (t) => t.palette.common.black,
        color: (t) => t.palette.common.white,
        borderRadius: 0.75,
      }}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', fontFamily: 'Roboto Condensed, sans-serif', justifySelf: 'center', color: '#fff' }}>Scene</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', fontFamily: 'Roboto Condensed, sans-serif', justifySelf: 'center', color: '#fff' }}>I/E</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', fontFamily: 'Roboto Condensed, sans-serif', color: '#fff' }}>Description</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', fontFamily: 'Roboto Condensed, sans-serif', justifySelf: 'center', color: '#fff' }}>ToD</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', fontFamily: 'Roboto Condensed, sans-serif', color: '#fff' }}>Cast</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', fontFamily: 'Roboto Condensed, sans-serif', justifySelf: 'center', color: '#fff' }}>Pages</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', fontFamily: 'Roboto Condensed, sans-serif', justifySelf: 'center', color: '#fff' }}>Est</Typography>
        {/* Secondary details (camera, props, etc.) move below each strip */}
      </Box>
    );
  };

  const getStripBorderColor = (strip, theme) => {
    const intExt = (strip.intExt || '').toUpperCase();
    const tod = (strip.effectiveDayNight || strip.dayNight || '').toUpperCase();
    // Solid hues to pair with the subtle backgrounds
    if (intExt.includes('INT') && !intExt.includes('EXT')) {
      if (tod.includes('DAY')) return '#BDBDBD'; // light grey
      if (tod.includes('NIGHT')) return '#2196F3'; // blue
      if (tod.includes('MORNING')) return '#00BCD4';
      if (tod.includes('EVENING')) return '#607D8B';
    }
    if (intExt.includes('EXT') && !intExt.includes('INT')) {
      if (tod.includes('DAY')) return '#FFEB3B'; // yellow
      if (tod.includes('NIGHT')) return '#673AB7'; // purple
      if (tod.includes('DAWN') || tod.includes('SUNRISE')) return '#FF9800';
      if (tod.includes('DUSK') || tod.includes('SUNSET')) return '#F44336';
      if (tod.includes('MORNING')) return '#8BC34A';
    }
    if (intExt.includes('I/E') || intExt.includes('INT/EXT')) {
      if (tod.includes('DAY')) return '#009688';
      if (tod.includes('NIGHT')) return '#673AB7';
    }
    if (tod.includes('CONT') || tod === 'CONT.') return '#9E9E9E';
    return '#616161';
  };

  // No camera notes syncing (removed)
  // Load minutesOverride from localStorage to survive refresh when no schedule exists
  useEffect(() => {
    try {
      if (!projectId) return;
      const raw = localStorage.getItem(`schedule_minutes_${projectId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setMinutesOverride(prev => ({ ...prev, ...parsed }));
      }
    } catch (e) {
      // ignore
    }
  }, [projectId]);
  // Persist minutesOverride to localStorage on change
  useEffect(() => {
    try {
      if (!projectId) return;
      localStorage.setItem(`schedule_minutes_${projectId}`, JSON.stringify(minutesOverride || {}));
    } catch (e) {
      // ignore
    }
  }, [projectId, minutesOverride]);

  // Persist SPECIAL EQUIPMENT map per project
  useEffect(() => {
    try {
      if (!projectId) return;
      const raw = localStorage.getItem(`schedule_special_equip_${projectId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setSpecialEquipMap(parsed);
      }
    } catch (e) {
      // ignore
    }
  }, [projectId]);
  useEffect(() => {
    try {
      if (!projectId) return;
      localStorage.setItem(`schedule_special_equip_${projectId}`, JSON.stringify(specialEquipMap || {}));
    } catch (e) {
      // ignore
    }
  }, [projectId, specialEquipMap]);

  // Persist manual category selections per project
  useEffect(() => {
    try {
      if (!projectId) return;
      const raw = localStorage.getItem(`schedule_cat_manual_${projectId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setCatManualMap(parsed);
      }
    } catch (e) {
      // ignore
    }
  }, [projectId]);
  useEffect(() => {
    try {
      if (!projectId) return;
      localStorage.setItem(`schedule_cat_manual_${projectId}`, JSON.stringify(catManualMap || {}));
    } catch (e) {
      // ignore
    }
  }, [projectId, catManualMap]);

  // Persist auto-tag overrides (remove/rename)
  useEffect(() => {
    try {
      if (!projectId) return;
      const raw = localStorage.getItem(`schedule_cat_overrides_${projectId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setCatOverrideMap(parsed);
      }
    } catch (e) {
      // ignore
    }
  }, [projectId]);
  useEffect(() => {
    try {
      if (!projectId) return;
      localStorage.setItem(`schedule_cat_overrides_${projectId}`, JSON.stringify(catOverrideMap || {}));
    } catch (e) {
      // ignore
    }
  }, [projectId, catOverrideMap]);

  // Helper: apply overrides to grouped breakdown tags for a scene
  const applyOverrides = (sid, groupedIn = {}) => {
    const grouped = groupedIn || {};
    const out = {};
    const ovrScene = catOverrideMap?.[sid] || {};
    Object.keys(grouped).forEach((cat) => {
      const items = Array.isArray(grouped[cat]) ? grouped[cat] : [];
      const removeSet = new Set((ovrScene?.[cat]?.remove || []).map(s => (s || '').toLowerCase()));
      const renameMap = ovrScene?.[cat]?.rename || {};
      const mapped = items.map(t => {
        const name = t?.name || t?.text || t?.title || '';
        const lowered = name.toLowerCase();
        if (!name) return null;
        if (removeSet.has(lowered)) return null;
        const renamed = renameMap[name] || renameMap[lowered] || name;
        return { ...t, name: renamed };
      }).filter(Boolean);
      out[cat] = mapped;
    });
    return out;
  };

  // Helper to resolve breakdown entry for a schedule strip by id or by scene number candidates
  const resolveBreakdownForStrip = (strip) => {
    const originalSid = String(strip.sceneId || strip.id || '');
    let sid = originalSid;
    let bd = sceneBreakdown[sid] || null;
    let fallbackUsed = false;
    const isEmptyGrouped = (x) => !x || !x.grouped || Object.keys(x.grouped || {}).length === 0;
    if (!bd || isEmptyGrouped(bd)) {
      // Try number candidates in order: strip.number, breakdown.sceneInfo.number, source scenes match
      const numCandidates = [];
      if (strip.number != null) numCandidates.push(strip.number);
      // If there is a (possibly empty) entry for originalSid, use its sceneInfo number
      const bdAtOriginal = sceneBreakdown[originalSid];
      if (bdAtOriginal?.sceneInfo?.number != null) numCandidates.push(bdAtOriginal.sceneInfo.number);
      // Try to find the source scene by id and use its number fields
      const sourceScene = (scenes || []).find(s => String(s.id || s._id || '') === originalSid);
      if (sourceScene) {
        numCandidates.push(
          sourceScene.sceneNumber?.standard,
          sourceScene.sceneNumber?.original,
          sourceScene.sceneNumber,
          sourceScene.number,
        );
      }
      // Normalize, find first that maps via scenesByNormNum and has breakdown
      const DEBUG_RESOLVE = false;
      for (const cand of numCandidates) {
        const key = normalizeSceneNumber(cand);
        if (!key) continue;
        // IMPORTANT: Only allow fallback resolution from the strip's own number.
        // Avoid borrowing via alternate candidate numbers unless the strip has no number.
        if (strip.number != null && normalizeSceneNumber(strip.number) !== key) {
          if (DEBUG_RESOLVE) {
            try { console.debug('[Resolver] Skipping non-strip candidate', { originalSid, stripNumber: strip.number, candidate: cand, key }); } catch {}
          }
          continue;
        }
        const sceneMatch = scenesByNormNum.get(key);
        const resolvedId = sceneMatch ? String(sceneMatch.id || sceneMatch._id || sceneMatch.sceneId) : null;
        if (resolvedId && sceneBreakdown[resolvedId]) {
          const candidateBd = sceneBreakdown[resolvedId];
          // Case A: No entry at originalSid at all -> adopt candidate even if empty (to preserve sceneInfo)
          if (!bd) {
            sid = String(resolvedId);
            bd = candidateBd;
            fallbackUsed = String(resolvedId) !== String(originalSid);
            if (DEBUG_RESOLVE) {
              try { console.debug('[Resolver] Adopted candidate due to missing original', { originalSid, stripNumber: strip.number, candidate: cand, key, resolvedId, fallbackUsed }); } catch {}
            }
            break;
          }
          // Case B: Have an entry but grouped is empty -> only replace if candidate has non-empty grouped
          if (isEmptyGrouped(bd) && !isEmptyGrouped(candidateBd)) {
            sid = String(resolvedId);
            bd = candidateBd;
            fallbackUsed = String(resolvedId) !== String(originalSid);
            if (DEBUG_RESOLVE) {
              try { console.debug('[Resolver] Replaced empty original with non-empty candidate', { originalSid, stripNumber: strip.number, candidate: cand, key, resolvedId, fallbackUsed }); } catch {}
            }
            break;
          }
        }
      }
    }
    return { sid, bd: bd || {}, fallbackUsed };
  };
  // Backend: Load prefs from server on mount (MongoDB) and debounce-save on changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!projectId) return;
        const prefs = await shootingScheduleService.getSchedulePrefs(projectId);
        if (cancelled || !prefs) return;
        // Merge: server data overlays current state without wiping local entries when server is empty
        if (prefs.specialEquipMap && typeof prefs.specialEquipMap === 'object') {
          setSpecialEquipMap(prev => ({ ...(prev || {}), ...(prefs.specialEquipMap || {}) }));
        }
        if (prefs.catManualMap && typeof prefs.catManualMap === 'object') {
          setCatManualMap(prev => ({ ...(prev || {}), ...(prefs.catManualMap || {}) }));
        }
        if (prefs.catOverrideMap && typeof prefs.catOverrideMap === 'object') {
          setCatOverrideMap(prev => ({ ...(prev || {}), ...(prefs.catOverrideMap || {}) }));
        }
        if (prefs.minutesOverride && typeof prefs.minutesOverride === 'object') {
          setMinutesOverride(prev => ({ ...(prev || {}), ...(prefs.minutesOverride || {}) }));
        }
      } catch (e) {
        // ignore fetch errors; localStorage fallback remains
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    const handler = setTimeout(() => {
      shootingScheduleService.saveSchedulePrefs(projectId, {
        specialEquipMap: specialEquipMap || {},
        catManualMap: catManualMap || {},
        catOverrideMap: catOverrideMap || {},
        minutesOverride: minutesOverride || {},
      }).catch(() => {/* ignore */});
    }, 600); // debounce 600ms
    return () => clearTimeout(handler);
  }, [projectId, specialEquipMap, catManualMap, catOverrideMap, minutesOverride]);
  // Preload minutesOverride from existing schedule, if available
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!projectId) return;
        const schedules = await shootingScheduleService.getShootingSchedules(projectId);
        if (!Array.isArray(schedules) || schedules.length === 0) return;
        const schedule = schedules[0];
        const scheduleId = schedule._id || schedule.id;
        if (!scheduleId) return;
        const full = await shootingScheduleService.getShootingScheduleById(scheduleId);
        const next = {};
        // Index current scenes by id and by normalized number for robust matching
        const byId = new Map();
        const byNum = new Map();
        (scenes || []).forEach(s => {
          const id = s.id || s._id;
          if (id) byId.set(String(id), s);
          const numKey = (s.sceneNumber?.standard || s.sceneNumber || s.number || '').toString().trim();
          if (numKey) byNum.set(numKey, s);
        });
        (full.shootingDays || []).forEach(day => {
          (day.scenes || []).forEach(scene => {
            const savedMinutes = (typeof scene.estimatedTime === 'number' && scene.estimatedTime >= 0)
              ? Math.round(scene.estimatedTime) : null;
            if (savedMinutes == null) return;
            const schedId = scene.sceneId || scene._id || scene.id;
            let targetScene = null;
            if (schedId && byId.has(String(schedId))) {
              targetScene = byId.get(String(schedId));
            } else {
              const numKey = (scene.sceneNumber || scene.number || '').toString().trim();
              if (numKey && byNum.has(numKey)) targetScene = byNum.get(numKey);
            }
            if (targetScene) {
              const targetId = targetScene.id || targetScene._id;
              if (targetId) next[String(targetId)] = savedMinutes;
            }
          });
        });
        if (!cancelled) setMinutesOverride(prev => ({ ...prev, ...next }));
      } catch (e) {
        // Non-fatal: just skip seeding if it fails
        console.warn('Failed to preload minutes from schedule', e);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, scenes]);

  // Offload schedule formatting to a Web Worker (with graceful fallback)
  const defaultTotals = { scenes: 0, pages: 0, pagesFmt: '0', eighths: 0, shootingDays: 0, avgPagesPerDay: 0 };
  const [formatted, setFormatted] = useState({ days: [], totals: defaultTotals });
  const workerRef = useRef(null);
  const reqIdRef = useRef(0);
  const latestIdRef = useRef(0);

  // Initialize worker once
  useEffect(() => {
    try {
      // eslint-disable-next-line no-undef
      workerRef.current = new Worker(new URL('../../workers/formatSchedule.worker.js', import.meta.url));
      workerRef.current.onmessage = (e) => {
        const { id, ok, result, error: errMsg } = e.data || {};
        if (id !== latestIdRef.current) return; // ignore stale responses
        if (ok) {
          setFormatted({ days: Array.isArray(result?.days) ? result.days : [], totals: result?.totals || defaultTotals });
        } else {
          console.warn('ShootingSchedule worker error:', errMsg);
          setError('Failed to format shooting schedule');
          setFormatted({ days: [], totals: defaultTotals });
        }
      };
    } catch (e) {
      // If worker creation fails, we'll rely on sync fallback below
      workerRef.current = null;
      dbg('Worker initialization failed, using sync formatting fallback', e);
    }
    return () => {
      try { workerRef.current?.terminate?.(); } catch {}
      workerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute on dependency changes
  useEffect(() => {
    const timingOpts = {
      maxCompanyMovesPerDay: 2,
      minutesPerEighth: 1.5,
      lunchMinutes: 30,
      moveMinutes: 20,
      targetDayMinutes: 12 * 60,
      dayStartTime: projectMeta?.callTime || undefined,
      minutesOverrideBySceneId: minutesOverride,
    };
    const id = ++reqIdRef.current;
    latestIdRef.current = id;

    if (workerRef.current) {
      try {
        workerRef.current.postMessage({ id, scenes, breaks, options: timingOpts });
        return; // wait for worker response
      } catch (e) {
        console.warn('Posting to schedule worker failed, falling back to sync compute', e);
      }
    }
    // Fallback synchronous compute
    try {
      const result = formatShootingSchedule(scenes, breaks, timingOpts);
      setFormatted({ days: Array.isArray(result?.days) ? result.days : [], totals: result?.totals || defaultTotals });
    } catch (e) {
      console.error('ShootingSchedule: formatter error (sync fallback)', e);
      setError('Failed to format shooting schedule');
      setFormatted({ days: [], totals: defaultTotals });
    }
  }, [scenes, breaks, projectMeta, minutesOverride]);

  const { days, totals } = formatted;

  // Minimal diagnostics to trace why scenes may not display
  useEffect(() => {
    try {
      const sCount = Array.isArray(scenes) ? scenes.length : 0;
      const bCount = Array.isArray(breaks) ? breaks.length : 0;
      const dCount = Array.isArray(days) ? days.length : 0;
      dbg('[ShootingSchedule] diagnostics', { scenes: sCount, breaks: bCount, days: dCount });
    } catch {}
  }, [scenes, breaks, days]);

  // Compute effective time-of-day per scene (inherit for CONT./LATER/SAME/IMMEDIATELY FOLLOWING)
  const daysWithEffectiveTOD = useMemo(() => {
    const normalize = (t) => (t || '').toString().toUpperCase().trim();
    const DEFINITIVE = new Set(['DAY','NIGHT','MORNING','EVENING','DAWN','DUSK','AFTERNOON','SUNSET','SUNRISE']);
    const INHERIT_MARKERS = [
      'CONTINUOUS','CONT.','CONT','CONT’D','CONT’', // include unicode apostrophe forms
      'LATER','MOMENTS LATER','SECONDS LATER','LATER THAT DAY',
      'SAME','IMMEDIATELY FOLLOWING','IMMEDIATELY','FOLLOWING'
    ];
    const isInheritance = (t) => {
      const tt = normalize(t);
      if (!tt) return true; // empty inherits
      if (DEFINITIVE.has(tt)) return false; // explicit definitive
      // Treat any known marker or phrases containing these markers as inheritance
      if (INHERIT_MARKERS.some(m => tt === m || tt.includes(m))) return true;
      // Otherwise, if not in definitive list, default to inheritance (safer for display)
      return true;
    };
    try {
      let carryDefinitive = null;
      let carryDefinitiveSceneNum = null;
      return (days || []).map(day => {
        // Seed with carry-over from previous day
        let lastDefinitive = carryDefinitive;
        let lastDefinitiveSceneNum = carryDefinitiveSceneNum;
        const newStrips = (day.strips || []).map(s => {
          if (s.type !== 'SCENE') return s;
          const tod = s.dayNight;
          let effective = tod;
          let effectiveInherited = false;
          let effectiveSourceSceneNum = null;
          if (isInheritance(tod)) {
            effective = lastDefinitive || tod;
            effectiveInherited = !!lastDefinitive;
            effectiveSourceSceneNum = lastDefinitiveSceneNum;
          } else {
            const norm = normalize(tod);
            lastDefinitive = DEFINITIVE.has(norm) ? norm : norm; // normalized definitive anchor
            lastDefinitiveSceneNum = s.number ?? s.sceneNumber?.standard ?? s.sceneNumber ?? s.displayIndex ?? null;
            // Display only canonical token for definitive ToD (e.g., DAY, NIGHT)
            effective = norm;
            effectiveInherited = false;
            effectiveSourceSceneNum = null;
          }
          return { ...s, effectiveDayNight: effective, effectiveInherited, effectiveSourceSceneNum };
        });
        // Update carry-over for next day using the last seen definitive in this day
        carryDefinitive = lastDefinitive;
        carryDefinitiveSceneNum = lastDefinitiveSceneNum;
        return { ...day, strips: newStrips };
      });
    } catch {
      return days || [];
    }
  }, [days]);

  // Diagnostic: summarize breakdown coverage across strips (must come after daysWithEffectiveTOD is defined)
  useEffect(() => {
    try {
      const totalDays = Array.isArray(daysWithEffectiveTOD) ? daysWithEffectiveTOD.length : 0;
      const allStrips = (daysWithEffectiveTOD || []).flatMap(d => d.strips || []).filter(s => s.type === 'SCENE');
      const withBD = allStrips.filter(s => !!sceneBreakdown[String(s.sceneId || s.id)]).length;
      const keys = Object.keys(sceneBreakdown || {});
      dbg('[ShootingSchedule] coverage', { totalDays, strips: allStrips.length, withBreakdown: withBD, breakdownKeys: keys.slice(0, 10), breakdownKeyCount: keys.length });
    } catch {}
  }, [daysWithEffectiveTOD, sceneBreakdown]);

  // Publish schedule-derived department items per day so Notes and other components consume the same data
  useEffect(() => {
    try {
      if (!projectId) return;
      const daysArr = Array.isArray(daysWithEffectiveTOD) ? daysWithEffectiveTOD : [];
      const publish = {};
      const getName = (t) => t?.text || t?.name || t?.title || '';
      const schedToDept = {
        LIGHTING: 'lighting',
        PROPS: 'props',
        SOUND: 'sound',
        VFX: 'vfx',
        VISUAL_EFFECTS: 'vfx',
        SPECIAL_EFFECTS: 'specialFx',
        VEHICLES: 'vehicles',
        STUNTS: 'stunts',
        ANIMALS: 'animals',
        HAIR: 'hairMakeup',
        MAKEUP: 'hairMakeup',
        HAIR_MAKEUP: 'hairMakeup',
        MISC: 'misc',
      };

      const DEBUG_LOG_FALLBACK = true;
      daysArr.forEach(day => {
        const agg = { camera: [], lighting: [], props: [], sound: [], vfx: [], specialFx: [], vehicles: [], stunts: [], animals: [], hairMakeup: [], misc: [] };
        (day.strips || []).forEach(strip => {
          if (strip.type !== 'SCENE') return;
          const originalSid = String(strip.sceneId || strip.id || '');
          const { sid, bd } = resolveBreakdownForStrip(strip);
          if (DEBUG_LOG_FALLBACK) {
            console.debug('[SchedulePub] Resolved', {
              day: day.day,
              stripNumber: strip.number,
              originalSid,
              resolvedSid: sid,
              hasBreakdownResolved: !!(bd && bd.grouped) && Object.keys(bd.grouped || {}).length > 0,
            });
          }
          const groupedRaw = DISABLE_AUTO_POPULATE ? {} : (bd.grouped || {});
          const grouped = applyOverrides(sid, groupedRaw);
          // Map grouped tags to departments
          Object.keys(grouped).forEach(cat => {
            const dept = schedToDept[cat];
            if (!dept) return;
            agg[dept] = agg[dept].concat((grouped[cat] || []).map(getName).filter(Boolean));
          });
          // Merge manual category entries for this scene
          const byCat = catManualMap[sid] || {};
          Object.keys(byCat).forEach(cat => {
            const dept = schedToDept[cat];
            if (!dept) return;
            agg[dept] = agg[dept].concat(((byCat[cat] || [])).filter(Boolean));
          });
          // Special equipment contributes to camera
          const equip = specialEquipMap[sid];
          if (Array.isArray(equip)) agg.camera = agg.camera.concat(equip.filter(Boolean));
        });
        // Deduplicate
        Object.keys(agg).forEach(k => {
          const set = new Set(agg[k].map(s => (s || '').trim()).filter(Boolean));
          agg[k] = Array.from(set);
        });
        publish[day.day] = agg; // day numbers are 1-based
      });

      const payload = { days: publish, updatedAt: Date.now() };
      localStorage.setItem(`schedule_pub_${projectId}`, JSON.stringify(payload));
      try {
        window.dispatchEvent(new CustomEvent('schedule_pub_updated', { detail: { projectId } }));
      } catch {}
    } catch (e) {
      // non-fatal
    }
  }, [projectId, daysWithEffectiveTOD, sceneBreakdown, catManualMap, specialEquipMap, scenesByNormNum]);

  // Helper: render count chips for all categories (used in day header)
  const renderCountChips = (counts = {}, options = {}) => {
    const exclude = options.exclude || [];
    const order = Object.keys(TAG_TYPES || {});
    const chips = [];
    const computedCounts = { ...counts };
    order.forEach(cat => {
      if (exclude.includes(cat)) return;
      const count = computedCounts[cat] || 0;
      if (count > 0) {
        const label = (TAG_TYPES[cat]?.label || cat);
        chips.push(<Chip key={cat} label={`${label} ${count}`} size="small" variant="outlined" />);
      }
    });
    return chips;
  };

  const renderBreakStrip = (strip, idx) => {
    const base = strip.label || (strip.type === 'COMPANY_MOVE' ? 'Company Move' : strip.type === 'LUNCH' ? 'Lunch' : strip.type);
    const label = typeof strip.durationMinutes === 'number' ? `${base} — ${strip.durationMinutes} min` : base;
    return (
      <Box key={`brk_${idx}`} sx={{ my: 1 }}>
        <Divider>
          <Chip label={label} size="small" variant="outlined" />
        </Divider>
      </Box>
    );
  };

  // Helper: per-scene detail chips with item names by category.
  const renderDetailChips = (grouped = {}, extraLists = {}) => {
    const chips = [];
    const displayLimit = 3;
    const safeList = (arr) => Array.isArray(arr) ? arr : [];
    const getName = (t) => t?.text || t?.name || t?.title || '';
    // Merge Extras categories into one
    const extrasList = [...safeList(grouped.EXTRAS), ...safeList(grouped.EXTRAS_SILENT)]
      .map(getName).filter(Boolean);
    if (extrasList.length) {
      const display = extrasList.slice(0, 6).join(', ');
      const more = extrasList.length - 6;
      const label = more > 0 ? `Extras: ${display} (+${more} more)` : `Extras: ${display}`;
      chips.push(
        SHOW_CATEGORY_CHIPS ? (
          <Tooltip key="EXTRAS_NAMES_TT" title={extrasList.join(', ')} arrow>
            <Chip label={label} size="small" variant="outlined" />
          </Tooltip>
        ) : (
          <Typography key="EXTRAS_NAMES_TXT" variant="body2" sx={{ color: '#888' }}>{label}</Typography>
        )
      );
    }

    // CAMERA chips removed

    // SPECIAL EQUIPMENT (custom inline category)
    const equipList = safeList(extraLists.SPECIAL_EQUIPMENT).filter(Boolean);
    if (equipList.length) {
      const label = `Camera: ${equipList.join(', ')}`;
      chips.push(
        SHOW_CATEGORY_CHIPS ? (
          <Chip key="EQUIP_NAMES_ALL" label={label} size="small" variant="outlined" />
        ) : (
          <Typography key="EQUIP_NAMES_TXT" variant="body2" sx={{ color: '#888' }}>{label}</Typography>
        )
      );
    }

    // Render all other categories present in TAG_TYPES (excluding Extras variants, CAMERA which we handled, and columns we render separately)
    const skip = new Set(['EXTRAS', 'EXTRAS_SILENT', 'CAMERA', 'PROPS', 'LIGHTING', 'VEHICLES', 'SOUND', 'STUNTS', 'SPECIAL_EFFECTS']);
    Object.keys(TAG_TYPES || {}).forEach(cat => {
      if (skip.has(cat)) return;
      const items = safeList(grouped[cat]).map(getName).filter(Boolean);
      if (!items.length) return;
      const pretty = TAG_TYPES[cat]?.label || cat;
      const label = `${pretty}: ${items.join(', ')}`;
      chips.push(
        SHOW_CATEGORY_CHIPS ? (
          <Chip key={`${cat}_NAMES_ALL`} label={label} size="small" variant="outlined" />
        ) : (
          <Typography key={`${cat}_NAMES_TXT`} variant="body2" sx={{ color: '#888' }}>{label}</Typography>
        )
      );
    });

    return chips;
  };

  // Header helpers
  const renderTopHeader = () => {
    const title = projectMeta?.title || projectMeta?.name;
    const company = projectMeta?.company;
    const director = projectMeta?.director;
    const producer = projectMeta?.producer;
    const firstAD = projectMeta?.firstAD || projectMeta?.firstAd;
    const version = projectMeta?.scheduleVersion;
    const versionDate = projectMeta?.scheduleDate || projectMeta?.updatedAt;
    const totalDaysPlanned = projectMeta?.totalShootingDays || totals?.shootingDays || (days?.length || 0);
    const hasAny = title || company || director || producer || firstAD || version || versionDate;
    if (!hasAny) return null;
    return (
      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: (t) => t.palette.mode === 'dark' ? '#000' : '#fff' }} className="print-avoid-break-inside">
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" useFlexGap flexWrap="wrap" spacing={2}>
          <Box>
            {title ? <Typography variant="h5" sx={{ fontWeight: 600 }}>{title}</Typography> : null}
            {company ? <Typography variant="body2">{company}</Typography> : null}
          </Box>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            {director ? <Chip label={`Director: ${director}`} size="small" /> : null}
            {producer ? <Chip label={`Producer: ${producer}`} size="small" /> : null}
            {firstAD ? <Chip label={`1st AD: ${firstAD}`} size="small" /> : null}
            {version ? <Chip label={`Version: ${version}`} size="small" /> : null}
            {versionDate ? <Chip label={`Date: ${new Date(versionDate).toLocaleDateString()}`} size="small" /> : null}
            {totalDaysPlanned ? <Chip color="primary" label={`Total Shooting Days: ${totalDaysPlanned}`} size="small" /> : null}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  

  const handleExportCSV = () => {
    try {
      const csvEscape = (val) => {
        const s = val == null ? '' : String(val);
        if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
        return s;
      };
      const CRLF = '\r\n';
      const rows = [];
      // Header row
      rows.push([
        'Day','Scene/Break','Number','INT/EXT','DAY/NIGHT','Set','Location','Pages (1/8)','Est Minutes',
        'Camera','Props','Lighting','Vehicles','Sound','VFX','Special FX','Stunts','Animals','Hair/Makeup','Misc','Warnings'
      ].map(csvEscape).join(','));
      daysWithEffectiveTOD.forEach(day => {
        const dayWarn = (day.warnings || []).map(w => w.message).join('|');
        day.strips.forEach(strip => {
          if (strip.type === 'SCENE') {
            const { sid, bd } = resolveBreakdownForStrip(strip);
            const equipList = Array.isArray(specialEquipMap[sid]) ? specialEquipMap[sid] : [];
            const groupedRaw = DISABLE_AUTO_POPULATE ? {} : (bd.grouped || {});
            const grouped = applyOverrides(sid, groupedRaw);
            const toNames = (arr) => (Array.isArray(arr) ? arr : []).map(t => t?.name || t?.text || t?.title || '').filter(Boolean);
            const propsList = toNames(grouped.PROPS);
            const lightList = toNames(grouped.LIGHTING);
            const vehList = toNames(grouped.VEHICLES);
            const soundList = toNames(grouped.SOUND);
            const vfxRaw = grouped.VFX || grouped.VISUAL_EFFECTS || [];
            const vfxList = toNames(vfxRaw);
            const sfxList = toNames(grouped.SPECIAL_EFFECTS);
            const stuntsList = toNames(grouped.STUNTS);
            const animalsList = toNames(grouped.ANIMALS);
            const hairList = toNames(grouped.HAIR);
            const makeupList = toNames(grouped.MAKEUP);
            const hairMakeupList = [...hairList, ...makeupList];
            const miscList = Array.isArray(catManualMap[sid]?.MISC) ? catManualMap[sid].MISC : [];
            const row = [
              day.day,
              'SCENE',
              strip.number ?? '',
              strip.intExt ?? '',
              (strip.effectiveDayNight || strip.dayNight || ''),
              strip.set ?? '',
              strip.location ?? '',
              formatPagesInEighths(strip.pages || 0),
              strip.estimatedMinutes ?? '',
              (equipList || []).join('|'),
              (propsList || []).join('|'),
              (lightList || []).join('|'),
              (vehList || []).join('|'),
              (soundList || []).join('|'),
              (vfxList || []).join('|'),
              (sfxList || []).join('|'),
              (stuntsList || []).join('|'),
              (animalsList || []).join('|'),
              (hairMakeupList || []).join('|'),
              (miscList || []).join('|'),
              dayWarn
            ];
            rows.push(row.map(csvEscape).join(','));
          } else {
            // Non-scene rows: align with header column count
            const row = [
              day.day,
              strip.type,
              '',
              '',
              '',
              '',
              '',
              '',
              strip.durationMinutes ?? '',
              '', '', '', '', '', '', '', '',
              '',
              '',
              '',
              dayWarn
            ];
            rows.push(row.map(csvEscape).join(','));
          }
        });
      });
      // Prepend BOM for Excel and use CRLF line endings
      const csvContent = '\uFEFF' + rows.join(CRLF) + CRLF;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'shooting-schedule.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV export failed', e);
      setError('Failed to export CSV');
    }
  };

  const renderDayHeader = (day) => {
    // Rollup counts across all categories for the day
    const rollupCounts = {};
    let equipmentTotal = 0;
    (day.strips || []).forEach(s => {
      if (s.type !== 'SCENE') return;
      const { sid, bd } = resolveBreakdownForStrip(s);
      if (!DISABLE_AUTO_POPULATE && bd && bd.counts) {
        Object.keys(bd.counts).forEach(cat => {
          rollupCounts[cat] = (rollupCounts[cat] || 0) + (bd.counts[cat] || 0);
        });
      }
      const eq = specialEquipMap[sid];
      if (Array.isArray(eq)) equipmentTotal += eq.length;
    });
    const totalDays = totals?.shootingDays || days?.length || 0;
    const dayLabel = totalDays ? `Day ${day.day} of ${totalDays}` : `Day ${day.day}`;
    const dateLabel = day.date ? new Date(day.date).toLocaleDateString() : null;
    return (
      <Box className="print-avoid-break-inside" sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: (t) => t.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1,
        borderBottom: (t) => `2px solid ${alpha(t.palette.primary.main, 0.15)}`,
        py: 1,
      }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{dayLabel}</Typography>
          {dateLabel ? <Chip variant="outlined" label={dateLabel} size="small" /> : null}
          {day.kpis.pages ? <Chip variant="outlined" label={`${formatPagesInEighths(day.kpis.pages)} pages`} size="small" /> : null}
          {day.kpis.companyMoves ? <Chip variant="outlined" label={`${day.kpis.companyMoves} moves`} size="small" /> : null}
          {typeof day.kpis.estimatedMinutes === 'number' ? <Chip variant="outlined" label={`${day.kpis.estimatedMinutes} min est.`} size="small" /> : null}
          {day.kpis.wrapTime ? <Chip color="success" label={`Wrap ~ ${day.kpis.wrapTime}`} size="small" /> : null}
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <Chip variant="outlined" label={`${day.kpis.scenes} scenes`} size="small" />
          <Chip variant="outlined" label={`${day.kpis.estimatedHours} hrs est.`} size="small" />
          <Chip variant="outlined" label={`${day.kpis.locations} locations`} size="small" />
          {renderCountChips(rollupCounts)}
          {equipmentTotal ? <Chip label={`Camera ${equipmentTotal}`} size="small" variant="outlined" /> : null}
          {(day.warnings || []).map((w, i) => (
            <Tooltip key={i} title={w.message} arrow>
              <Chip icon={<WarningIcon sx={{ fontSize: 16 }} />} color="warning" label={w.type} size="small" />
            </Tooltip>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderSceneStrip = (strip, idx) => {
    const { sid, bd, fallbackUsed } = resolveBreakdownForStrip(strip);
    const counts = DISABLE_AUTO_POPULATE ? {} : (bd.counts || {});
    const groupedRaw = DISABLE_AUTO_POPULATE ? {} : (bd.grouped || {});
    const grouped = applyOverrides(sid, groupedRaw);
    const equipList = Array.isArray(specialEquipMap[sid]) ? specialEquipMap[sid] : [];
    const manualByCat = catManualMap[sid] || {};
    const eqDraft = equipDraftMap[sid] || '';
    const propsNames = (Array.isArray(grouped.PROPS) ? grouped.PROPS : [])
      .map(t => t?.name || t?.text || t?.title || '')
      .filter(Boolean);

    // Derive Story Day and Cast summary from breakdown if available
    const storyDay = bd.scriptDay || bd.storyDay || strip.scriptDay || null;
    // Pull cast directly from OneLiner scenes data when available (fast path lookups)
    const sidStr = String(sid);
    let sceneObj = scenesById.get(sidStr);
    if (!sceneObj) {
      const numKeyB = normalizeSceneNumber(strip.number || '');
      if (numKeyB) sceneObj = scenesByNormNum.get(numKeyB);
    }
    const listFrom = (arr) => (Array.isArray(arr) ? arr : []).map(t => t?.name || t?.text || t?.title || '').filter(Boolean);
    // Use ONLY OneLiner-derived characters on the scene object for cast column
    let castList = Array.isArray(sceneObj?.characters) ? [...sceneObj.characters] : [];
    // Unify with OneLiner: match names to project cast and sort by hierarchy (even though we'll show only count)
    const castWithMeta = (castList || []).map(name => {
      const cm = (castMembers || []).find(c => (c.characterName || '').toLowerCase() === (name || '').toLowerCase());
      return { name, member: cm, hierarchy: cm?.hierarchy ?? Infinity };
    }).sort((a, b) => (a.hierarchy === b.hierarchy) ? a.name.localeCompare(b.name) : (a.hierarchy - b.hierarchy));
    const castDisplay = (() => {
      if (!castWithMeta.length) return null;
      const names = castWithMeta.map(c => c.name);
      return { count: names.length, full: names.join(', '), list: castWithMeta };
    })();

    const addEquipment = () => {
      const val = sanitize(eqDraft);
      if (!val) return;
      if (tooLong(val)) return setError(`Equipment item exceeds ${MAX_ITEM_LEN} chars`);
      const list = Array.isArray(specialEquipMap[sid]) ? [...specialEquipMap[sid]] : [];
      const seen = makeSet(list);
      if (seen.has(val.toLowerCase())) return setError('Duplicate equipment');
      list.push(val);
      const next = { ...specialEquipMap, [sid]: list };
      setSpecialEquipMap(next);
      setEquipDraftMap({ ...equipDraftMap, [sid]: '' });
      if (typeof onUpdateSpecialEquipment === 'function') onUpdateSpecialEquipment(sid, list);
    };
    const removeEquipment = (index) => {
      const list = Array.isArray(specialEquipMap[sid]) ? [...specialEquipMap[sid]] : [];
      if (index < 0 || index >= list.length) return;
      list.splice(index, 1);
      const next = { ...specialEquipMap, [sid]: list };
      setSpecialEquipMap(next);
      if (typeof onUpdateSpecialEquipment === 'function') onUpdateSpecialEquipment(sid, list);
    };
    const startEditMinutes = () => {
      const current = (typeof strip.estimatedMinutes === 'number') ? String(strip.estimatedMinutes) : '';
      setEditingMinutesFor(sid);
      setMinutesDraft(current);
    };
    const commitMinutes = async () => {
      const val = minutesDraft.trim();
      if (val === '') { setEditingMinutesFor(null); return; }
      const n = Number(val);
      if (!isFinite(n) || n < 0) { setError('Minutes must be a non-negative number'); return; }
      const rounded = Math.round(n);
      setMinutesOverride(prev => ({ ...prev, [sid]: rounded }));
      setEditingMinutesFor(null);
      // Attempt to persist to an existing schedule if available
      try {
        if (!projectId) return; // need project context
        const schedules = await shootingScheduleService.getShootingSchedules(projectId);
        if (!Array.isArray(schedules) || schedules.length === 0) {
          console.info('No shooting schedule exists yet; minutes override kept locally only.');
          return;
        }
        const schedule = schedules[0];
        const scheduleId = schedule._id || schedule.id;
        if (!scheduleId) return;
        const full = await shootingScheduleService.getShootingScheduleById(scheduleId);
        const daysArr = Array.isArray(full.shootingDays) ? [...full.shootingDays] : [];
        let updated = false;
        for (const day of daysArr) {
          if (!Array.isArray(day.scenes)) continue;
          for (const scene of day.scenes) {
            const sceneId = scene.sceneId || scene._id || scene.id;
            if (sceneId && sceneId === sid) {
              scene.estimatedTime = rounded; // minutes
              updated = true;
              break;
            }
          }
          if (updated) break;
        }
        if (updated) {
          await shootingScheduleService.updateShootingSchedule(scheduleId, {
            title: full.title,
            startDate: full.startDate,
            endDate: full.endDate,
            status: full.status,
            shootingDays: daysArr,
          });
        } else {
          console.warn('Edited scene not found in existing schedule; consider regenerating schedule.');
        }
      } catch (e) {
        console.error('Failed to persist minutes to schedule', e);
        setError('Failed to save minutes to schedule');
      }
    };
    const cancelMinutes = () => {
      setEditingMinutesFor(null);
      setMinutesDraft('');
    };
    return (
      <Box key={idx} sx={{
        display: 'block',
        p: 0.75,
        borderRadius: 1,
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        mb: 1,
        backgroundColor: (theme) => `${getStripBgColor(strip, theme)} !important`,
        borderLeft: (theme) => `6px solid ${getStripBorderColor(strip, theme)}`,
        boxShadow: (t) => `inset 0 0 0 1px ${alpha(t.palette.common.black, t.palette.mode === 'dark' ? 0.25 : 0.04)}`,
        transition: 'box-shadow 180ms ease, transform 180ms ease',
        '&:hover': {
          boxShadow: (t) => `${t.shadows[2]}, inset 0 0 0 1px ${alpha(t.palette.common.black, t.palette.mode === 'dark' ? 0.25 : 0.04)}`,
        },
        '@media print': {
          breakInside: 'avoid',
          pageBreakInside: 'avoid',
          marginBottom: '8px',
          padding: '6px',
        },
      }}>
        {/* Top row: primary columns */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '84px 68px minmax(240px, 1fr) 92px minmax(80px, max-content) 68px 80px',
          alignItems: 'center',
          columnGap: 0.9,
          rowGap: 0.75,
          backgroundColor: (t) => t.palette.common.black,
          color: (t) => t.palette.common.white,
          borderRadius: 0.75,
          px: 1,
          py: 0.75,
        }}>
          {/* Col 1: Scene Number */}
          <Box sx={{ justifySelf: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'Roboto Condensed, sans-serif' }}>{strip.number}</Typography>
            {fallbackUsed ? (
              <Typography variant="caption" sx={{ mt: 0.25, display: 'block', color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>fallback</Typography>
            ) : null}
          </Box>

          {/* Col 2: I/E (plain text) */}
          <Typography
            variant="body2"
            sx={{ justifySelf: 'center', color: '#fff', fontWeight: 700 }}
          >
            {(strip.intExt || '').toString().toUpperCase()}
          </Typography>

          {/* Col 3: Description */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
            {strip.set ? (
              <Typography 
                variant="caption" 
                sx={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic', fontSize: '0.75rem' }}
              >
                {strip.set}
              </Typography>
            ) : null}
            <Tooltip title={sceneObj?.description || strip.location || ''} arrow>
              <Typography 
                variant="body2" 
                sx={{ fontSize: '0.875rem', lineHeight: 1.4, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {sceneObj?.description
                  ? sceneObj.description.split('\n')[0]
                  : (strip.location ? `${strip.location}` : '')
                }
              </Typography>
            </Tooltip>
          </Box>

          {/* Col 4: Time of Day (plain text) */}
          {(() => {
            const text = (strip.effectiveDayNight || strip.dayNight || '').toString().toUpperCase();
            const inherited = !!strip.effectiveInherited;
            const fromScene = strip.effectiveSourceSceneNum;
            const content = (
              <Typography 
                variant="body2"
                sx={{ justifySelf: 'center', color: '#fff', fontWeight: 700 }}
              >
                {text}
              </Typography>
            );
            return inherited && (fromScene !== null && fromScene !== undefined) ? (
              <Tooltip title={`Inherited from Scene ${fromScene}`} arrow>
                <span>{content}</span>
              </Tooltip>
            ) : content;
          })()}

          {/* Col 4: Cast numbers and color codes (all cast) */}
          <Box sx={{ justifySelf: 'center' }}>
            {castDisplay ? (
              <Tooltip title={castDisplay.full} arrow>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {castDisplay.list
                    .filter((c) => hasHierarchy(c.member?.hierarchy))
                    .map((c, i) => {
                      const style = getHierarchyStyle(c.member?.hierarchy, theme);
                      const num = c.member?.hierarchy;
                      const key = `${c.name || ''}_${num || ''}_${i}`;
                      return (
                        <Avatar
                          key={key}
                          sx={{
                            width: 20,
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: style.border, // match OneLiner avatar color
                            color: '#000',         // black text like OneLiner
                            fontWeight: 700,
                          }}
                          title={`${c.name} (#${num})`}
                        >
                          {num}
                        </Avatar>
                      );
                    })}
                </Stack>
              </Tooltip>
            ) : (
              <Box sx={{ justifySelf: 'center' }} />
            )}
          </Box>

          {/* Col 5: Pages (plain text) */}
          <Typography 
            variant="body2"
            sx={{ justifySelf: 'center', color: '#fff', fontWeight: 700 }} 
          >
            {formatPagesInEighths(strip.pages || 0)}
          </Typography>

          {/* Col 6: Estimated minutes with inline editor (plain text when not editing) */}
          {editingMinutesFor === sid ? (
            <TextField
              size="small"
              value={minutesDraft}
              onChange={(e) => setMinutesDraft(e.target.value.replace(/[^0-9.]/g, ''))}
              onBlur={commitMinutes}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitMinutes();
                if (e.key === 'Escape') cancelMinutes();
              }}
              sx={{ width: 100, ...textFieldSx }}
              autoFocus
              inputProps={{ inputMode: 'numeric' }}
            />
          ) : (
            <Typography
              variant="body2"
              onClick={startEditMinutes}
              title="Click to edit minutes"
              sx={{ cursor: 'pointer', fontWeight: 700, fontSize: '0.875rem', fontFamily: 'Roboto Condensed, sans-serif', justifySelf: 'center', color: '#fff' }}
            >
              {`${typeof strip.estimatedMinutes === 'number' ? strip.estimatedMinutes : ''} min`}
            </Typography>
          )}
        </Box>

        {/* Second row: labeled sections for Camera and tag categories */}
        <Box sx={{ mt: 1 }}>
          {/* Switch to grid for consistent column alignment across rows */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                // md+: Camera + 6 data columns (5 paired columns + Special FX)
                md: 'minmax(260px, 1fr) repeat(6, minmax(200px, 1fr))',
              },
              columnGap: 2,
              rowGap: 1,
              '@media print': { columnGap: 1.5, rowGap: 1 },
            }}
          >
            {/* Camera section (first column, top row) */}
            <Box sx={{ pl: { xs: 0, md: 0 }, gridColumn: { md: 1 }, gridRow: { md: 1 } }} className="no-print">
              <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                <IconButton
                  size="small"
                  aria-label="add camera gear"
                  onClick={() => startTransition(() => {
                    // Exclusive open within this scene: close all category editors and their custom inputs
                    setCatEditorOpen(prev => ({ ...prev, [sid]: {} }));
                    setCatCustomOpen(prev => ({ ...prev, [sid]: {} }));
                    setCameraCustomSid(null);
                    setEquipEditorOpenSid(prev => (prev === sid ? null : sid));
                  })}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <Typography variant="overline" sx={{ color: (t) => t.palette.text.secondary, letterSpacing: 0.6 }}>Camera</Typography>
              </Stack>
              {/* Existing selections */}
              <TagChipList
                names={equipList}
                idPrefix={`camera_${sid}`}
                chipSx={{ 
                  borderRadius: 999, 
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.16), 
                  color: (t) => t.palette.mode === 'dark' ? '#fff' : '#111' 
                }}
                onDelete={(name) => {
                  const nextList = (Array.isArray(equipList) ? equipList : []).filter(n => n !== name);
                  const clean = nextList.map(sanitize).filter(Boolean).slice(0, 20);
                  const unique = Array.from(makeSet(clean));
                  const next = { ...specialEquipMap, [sid]: unique };
                  setSpecialEquipMap(next);
                  // immediate persist to backend
                  if (projectId) {
                    shootingScheduleService.saveSchedulePrefs(projectId, {
                      specialEquipMap: next,
                      catManualMap: catManualMap || {},
                    }).catch(() => {});
                  }
                  if (typeof onUpdateSpecialEquipment === 'function') onUpdateSpecialEquipment(sid, nextList);
                }}
              />
              {/* Dropdown editor, shown when + is clicked */}
              <CameraEquipAutocomplete
                open={equipEditorOpenSid === sid}
                sid={sid}
                equipList={equipList}
                options={ALL_EQUIP_OPTIONS}
                textFieldSx={textFieldSx}
                onChange={(list) => {
                  // If user chose the Custom option, prompt for a custom value
                  if (list.includes(CUSTOM_OPTION)) {
                    list = list.filter((v) => v !== CUSTOM_OPTION);
                    setCameraCustomSid(sid);
                  }
                  const clean = list.map(sanitize).filter(Boolean).slice(0, 20);
                  const unique = Array.from(makeSet(clean));
                  const next = { ...specialEquipMap, [sid]: unique };
                  setSpecialEquipMap(next);
                  // immediate persist to backend
                  if (projectId) {
                    shootingScheduleService.saveSchedulePrefs(projectId, {
                      specialEquipMap: next,
                      catManualMap: catManualMap || {},
                    }).catch(() => {});
                  }
                  if (typeof onUpdateSpecialEquipment === 'function') onUpdateSpecialEquipment(sid, list);
                }}
                onBlur={() => setEquipEditorOpenSid(null)}
              />
              {cameraCustomSid === sid ? (
                <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                  <TextField
                    size="small"
                    placeholder="Type custom camera gear and press Enter"
                    value={cameraCustomDraft}
                    onChange={(e) => setCameraCustomDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const entered = sanitize(cameraCustomDraft).trim();
                        if (!entered) return;
                        const base = Array.isArray(specialEquipMap[sid]) ? specialEquipMap[sid] : [];
                        const clean = [...base, entered].map(sanitize).filter(Boolean).slice(0, 20);
                        const unique = Array.from(makeSet(clean));
                        const next = { ...specialEquipMap, [sid]: unique };
                        setSpecialEquipMap(next);
                        setCameraCustomDraft('');
                        setCameraCustomSid(null);
                        if (projectId) {
                          shootingScheduleService.saveSchedulePrefs(projectId, {
                            specialEquipMap: next,
                            catManualMap: catManualMap || {},
                          }).catch(() => {});
                        }
                        if (typeof onUpdateSpecialEquipment === 'function') onUpdateSpecialEquipment(sid, unique);
                      }
                      if (e.key === 'Escape') {
                        setCameraCustomSid(null);
                        setCameraCustomDraft('');
                      }
                    }}
                    sx={{ minWidth: 260, ...textFieldSx }}
                  />
                </Stack>
              ) : null}
            </Box>
            {/* Tag sections with add (+) and manual entries */}
            {/* Explicit 2-row placement mapping for categories */}
            {SCHEDULE_TAG_COLUMNS.map((col) => {
              const bdNames = getNamesFromGrouped(grouped, col.keys);
              const bdSet = Array.from(new Set((bdNames || []).map((n) => n.trim()).filter(Boolean)));
              const manualList = Array.isArray(manualByCat[col.key]) ? manualByCat[col.key] : [];
              const isOpen = !!(catEditorOpen[sid]?.[col.key]);
              const cat = CATEGORY_STYLES[col.key] || {};
              const chipSx = {
                backgroundColor: cat.bg,
                borderColor: cat.bd,
                color: (t) => t.palette.mode === 'dark' ? '#fff' : '#111',
                borderRadius: 999,
                '& .MuiChip-label': { px: 0.75, fontWeight: 500 },
              };
              const baseOptions = BASE_CAT_OPTIONS[col.key] || [CUSTOM_OPTION];
              const openEditor = () => {
                // Close camera editors for this scene and any other category editors, then open this one
                setEquipEditorOpenSid(null);
                setCameraCustomSid(null);
                setCatCustomOpen(prev => ({ ...prev, [sid]: {} }));
                setCatEditorOpen(prev => ({ ...prev, [sid]: { [col.key]: true } }));
              };
              const toggleEditor = () => {
                setEquipEditorOpenSid(null);
                setCameraCustomSid(null);
                setCatCustomOpen(prev => ({ ...prev, [sid]: {} }));
                setCatEditorOpen(prev => {
                  const isOpenNow = !!prev[sid]?.[col.key];
                  return { ...prev, [sid]: isOpenNow ? {} : { [col.key]: true } };
                });
              };
              const setManualList = (nextList) => setCatManualMap(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [col.key]: nextList } }));
              // Grid placement for pairs; Special FX stays on top row, last column
              const placement = {
                LIGHTING: { col: 2, row: 1 },
                PROPS: { col: 3, row: 1 },
                SOUND: { col: 4, row: 1 },
                VFX: { col: 5, row: 1 },
                SPECIAL_EFFECTS: { col: 6, row: 1 },
                VEHICLES: { col: 1, row: 2 },
                STUNTS: { col: 2, row: 2 },
                ANIMALS: { col: 3, row: 2 },
                HAIR_MAKEUP: { col: 4, row: 2 },
              }[col.key] || { col: 'auto', row: 'auto' };
              const hasLeftBorder = typeof placement.col === 'number' && placement.col > 1;
              return (
                <Box
                  key={`col_${sid}_${col.label}`}
                  sx={{
                    pl: { xs: 0, md: hasLeftBorder ? 1.5 : 0 },
                    borderLeft: { xs: 'none', md: hasLeftBorder ? (t) => `1px solid ${alpha(t.palette.divider, 0.4)}` : 'none' },
                    gridColumn: { md: placement.col },
                    gridRow: { md: placement.row },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                    <IconButton size="small" aria-label={`add ${col.label.toLowerCase()}`} onClick={() => startTransition(() => toggleEditor())}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="overline" sx={{ color: (t) => t.palette.text.secondary, letterSpacing: 0.6 }}>{col.label}</Typography>
                  </Stack>
                  {/* Unified chips: merge breakdown+manual for a single row */}
                  {(() => {
                    // Build unified list; prefer showing a single chip if present in both, marked as breakdown (non-deletable)
                    const manualSetLC = new Set((manualList || []).map(v => (v || '').toLowerCase()));
                    const items = [];
                    (bdSet || []).forEach(n => {
                      if (!n) return;
                      items.push({ name: n, source: 'breakdown', deletable: false });
                      manualSetLC.delete((n || '').toLowerCase());
                    });
                    (manualList || []).forEach(n => {
                      if (!n) return;
                      // remaining manual only
                      if (!items.find(it => it.name.toLowerCase() === n.toLowerCase())) {
                        items.push({ name: n, source: 'manual', deletable: true });
                      }
                    });
                    return (
                      <TagChipList
                        items={items}
                        idPrefix={`${col.label.toLowerCase()}_all_${sid}`}
                        chipSx={chipSx}
                        onDelete={(name) => {
                          // delete only affects manual list
                          const nextList = (Array.isArray(manualList) ? manualList : []).filter(n => n.toLowerCase() !== name.toLowerCase());
                          setManualList(nextList);
                          if (projectId) {
                            const nextCatMap = { ...(catManualMap[sid] || {}), [col.key]: nextList };
                            const merged = { ...(catManualMap || {}), [sid]: nextCatMap };
                            shootingScheduleService.saveSchedulePrefs(projectId, {
                              specialEquipMap: specialEquipMap || {},
                              catManualMap: merged,
                            }).catch(() => {});
                          }
                        }}
                        onAutoRemove={(name) => {
                          setCatOverrideMap(prev => {
                            const sceneOvr = { ...(prev[sid] || {}) };
                            const keys = Array.isArray(col.keys) ? col.keys : [col.key];
                            const nextScene = { ...sceneOvr };
                            keys.forEach(k => {
                              const catOvr = { remove: [], rename: {}, ...(nextScene[k] || {}) };
                              const rmSet = new Set([...(catOvr.remove || []), name]);
                              nextScene[k] = { ...catOvr, remove: Array.from(rmSet) };
                            });
                            const next = { ...(prev || {}), [sid]: nextScene };
                            return next;
                          });
                          if (projectId) {
                            // Persist for all equivalent keys
                            const keys = Array.isArray(col.keys) ? col.keys : [col.key];
                            const sceneOvr = { ...(catOverrideMap[sid] || {}) };
                            keys.forEach(k => {
                              const catOvr = sceneOvr[k] || {};
                              const removeArr = Array.from(new Set([...(catOvr.remove || []), name]));
                              sceneOvr[k] = { ...catOvr, remove: removeArr };
                            });
                            shootingScheduleService.saveSchedulePrefs(projectId, {
                              specialEquipMap: specialEquipMap || {},
                              catManualMap: catManualMap || {},
                              catOverrideMap: { ...catOverrideMap, [sid]: sceneOvr },
                            }).catch(() => {});
                          }
                        }}
                      />
                    );
                  })()}
                  {/* Dropdown editor for manual additions (eager-mounted) */}
                  <CategoryTagAutocomplete
                    open={isOpen}
                    options={[...baseOptions, ...bdSet]}
                    value={manualList}
                    placeholder={`Select or type ${col.label.toLowerCase()}`}
                    textFieldSx={textFieldSx}
                    onChange={(list) => {
                        if (list.includes(CUSTOM_OPTION)) {
                          list = list.filter((v) => v !== CUSTOM_OPTION);
                          setCatCustomOpen(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [col.key]: true } }));
                          // do not add here; wait for inline custom submit
                        }
                        const clean = list.map(sanitize).filter(Boolean).slice(0, 20);
                        // Uniqueness preserving original casing
                        const deduped = [];
                        const seen = new Set();
                        clean.forEach((v) => { const key = (v || '').toLowerCase(); if (!seen.has(key)) { seen.add(key); deduped.push(v); } });
                        setManualList(deduped);
                        if (projectId) {
                          const nextCatMap = { ...(catManualMap[sid] || {}), [col.key]: deduped };
                          const merged = { ...(catManualMap || {}), [sid]: nextCatMap };
                          shootingScheduleService.saveSchedulePrefs(projectId, {
                            specialEquipMap: specialEquipMap || {},
                            catManualMap: merged,
                          }).catch(() => {});
                        }
                      }}
                    onBlur={() => setCatEditorOpen(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [col.key]: false } }))}
                    sx={{ display: isOpen ? 'block' : 'none' }}
                  />
                  {catCustomOpen[sid]?.[col.key] ? (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                      <TextField
                        size="small"
                        placeholder={`Type custom ${col.label.toLowerCase()} and press Enter`}
                        value={catCustomDraft[sid]?.[col.key] || ''}
                        onChange={(e) => setCatCustomDraft(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [col.key]: e.target.value } }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const entered = sanitize(catCustomDraft[sid]?.[col.key] || '').trim();
                            if (!entered) return;
                            const base = Array.isArray(manualList) ? manualList : [];
                            const clean = [...base, entered].map(sanitize).filter(Boolean).slice(0, 20);
                            const deduped = [];
                            const seen = new Set();
                            clean.forEach((v) => { const key = (v || '').toLowerCase(); if (!seen.has(key)) { seen.add(key); deduped.push(v); } });
                            setManualList(deduped);
                            setCatCustomDraft(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [col.key]: '' } }));
                            setCatCustomOpen(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [col.key]: false } }));
                            if (projectId) {
                              const nextCatMap = { ...(catManualMap[sid] || {}), [col.key]: deduped };
                              const merged = { ...(catManualMap || {}), [sid]: nextCatMap };
                              shootingScheduleService.saveSchedulePrefs(projectId, {
                                specialEquipMap: specialEquipMap || {},
                                catManualMap: merged,
                              }).catch(() => {});
                            }
                          }
                          if (e.key === 'Escape') {
                            setCatCustomOpen(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [col.key]: false } }));
                            setCatCustomDraft(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [col.key]: '' } }));
                          }
                        }}
                        sx={{ minWidth: 220, ...textFieldSx }}
                      />
                    </Stack>
                  ) : null}
                </Box>
              );
            })}
            {/* Misc (manual only, placed bottom row, column 5) */}
            <Box sx={{ pl: { xs: 0, md: 1.5 }, borderLeft: { xs: 'none', md: (t) => `1px solid ${alpha(t.palette.divider, 0.4)}` }, gridColumn: { md: 5 }, gridRow: { md: 2 } }}>
              <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                <IconButton
                  size="small"
                  aria-label="add misc"
                  onClick={() => startTransition(() => {
                    // Exclusive open: close camera and other cats for this scene
                    setEquipEditorOpenSid(null);
                    setCameraCustomSid(null);
                    setCatCustomOpen(prev => ({ ...prev, [sid]: {} }));
                    setCatEditorOpen(prev => {
                      const isOpenNow = !!prev[sid]?.MISC;
                      return { ...prev, [sid]: isOpenNow ? {} : { MISC: true } };
                    });
                  })}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <Typography variant="overline" sx={{ color: (t) => t.palette.text.secondary, letterSpacing: 0.6 }}>Misc.</Typography>
              </Stack>
              {/* Story day chip remains */}
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 0.5 }}>
                {storyDay != null ? (
                  <Chip variant="outlined" label={`Story Day ${storyDay}`} size="small" />
                ) : null}
              </Stack>
              {/* Manual Misc chips */}
              {(() => {
                const miscList = Array.isArray(manualByCat.MISC) ? manualByCat.MISC : [];
                const chipSx = {
                  backgroundColor: CATEGORY_STYLES.MISC.bg,
                  borderColor: CATEGORY_STYLES.MISC.bd,
                  color: (t) => t.palette.mode === 'dark' ? '#fff' : '#111',
                  borderRadius: 999,
                  '& .MuiChip-label': { px: 0.75, fontWeight: 500 },
                };
                const setMiscList = (nextList) => setCatManualMap(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), MISC: nextList } }));
                const isOpen = !!(catEditorOpen[sid]?.MISC);
                return (
                  <>
                    {SHOW_CATEGORY_CHIPS ? (
                      <TagChipList
                        names={miscList}
                        idPrefix={`misc_manual_${sid}`}
                        chipSx={chipSx}
                        onDelete={(name) => {
                          const nextList = (Array.isArray(miscList) ? miscList : []).filter(n => n !== name);
                          setMiscList(nextList);
                        }}
                      />
                    ) : (
                      (Array.isArray(miscList) && miscList.length > 0) ? (
                        <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
                          {miscList.join(', ')}
                        </Typography>
                      ) : null
                    )}
                    {/* Keep editor mounted; toggle visibility via open/display */}
                    <CategoryTagAutocomplete
                      open={isOpen}
                      options={MISC_OPTIONS}
                      value={miscList}
                      placeholder="Select or type misc item"
                      textFieldSx={textFieldSx}
                      onChange={(list) => {
                          if (list.includes(CUSTOM_OPTION)) {
                            list = list.filter((v) => v !== CUSTOM_OPTION);
                            setCatCustomOpen(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), MISC: true } }));
                          }
                          const clean = list.map(sanitize).filter(Boolean).slice(0, 20);
                          const deduped = [];
                          const seen = new Set();
                          clean.forEach((v) => { const key = (v || '').toLowerCase(); if (!seen.has(key)) { seen.add(key); deduped.push(v); } });
                          setMiscList(deduped);
                        }}
                      onBlur={() => setCatEditorOpen(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), MISC: false } }))}
                      sx={{ display: isOpen ? 'block' : 'none' }}
                    />
                    {catCustomOpen[sid]?.MISC ? (
                      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                        <TextField
                          size="small"
                          placeholder="Type custom misc item and press Enter"
                          value={catCustomDraft[sid]?.MISC || ''}
                          onChange={(e) => setCatCustomDraft(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), MISC: e.target.value } }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const entered = sanitize(catCustomDraft[sid]?.MISC || '').trim();
                              if (!entered) return;
                              const base = Array.isArray(miscList) ? miscList : [];
                              const clean = [...base, entered].map(sanitize).filter(Boolean).slice(0, 20);
                              const deduped = [];
                              const seen = new Set();
                              clean.forEach((v) => { const key = (v || '').toLowerCase(); if (!seen.has(key)) { seen.add(key); deduped.push(v); } });
                              setMiscList(deduped);
                              setCatCustomDraft(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), MISC: '' } }));
                              setCatCustomOpen(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), MISC: false } }));
                            }
                            if (e.key === 'Escape') {
                              setCatCustomOpen(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), MISC: false } }));
                              setCatCustomDraft(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), MISC: '' } }));
                            }
                          }}
                          sx={{ minWidth: 220, ...textFieldSx }}
                        />
                      </Stack>
                    ) : null}
                  </>
                );
              })()}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {renderTopHeader()}
      <Paper elevation={0} sx={{ 
        p: 2, 
        mb: 2, 
        borderRadius: 1,
        backgroundColor: (t) => t.palette.mode === 'dark' ? '#000' : '#fff',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 16px rgba(0,0,0,0.3)'
          : '0 2px 8px rgba(0,0,0,0.06)'
      }} className="no-print">
        <Stack direction="row" spacing={1}>
          <Button startIcon={<PrintIcon />} variant="outlined" onClick={handlePrint}>Print</Button>
          <Button startIcon={<FileDownloadIcon />} variant="outlined" onClick={handleExportCSV}>Export CSV</Button>
        </Stack>
      </Paper>

      {/* Error banner */}
      {!!error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Totals */}
      <Paper className="print-avoid-break-inside" sx={{ 
        p: 2, 
        mb: 2, 
        borderRadius: 1,
        backgroundColor: (t) => t.palette.mode === 'dark' ? '#000' : '#fff',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 16px rgba(0,0,0,0.3)'
          : '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip label={`${totals.shootingDays} shooting days`} />
          <Chip label={`${totals.scenes} scenes`} />
          <Chip label={`${totals.pagesFmt} pages total`} />
          <Chip label={`${Math.round(totals.eighths)} eighths`} />
          <Chip label={`Avg ${totals.avgPagesPerDay.toFixed ? totals.avgPagesPerDay.toFixed(1) : totals.avgPagesPerDay} pages/day`} />
        </Stack>
      </Paper>

      {/* Empty state */}
      {(!days || days.length === 0) && (
        <Alert severity="info">
          No scenes found for this project. Once scenes and day breaks are available, the schedule will render here.
        </Alert>
      )}

      {/* Days */}
      {daysWithEffectiveTOD.map((day, i) => (
        <Paper key={i} className="print-avoid-break-inside" sx={{ 
          p: 2, 
          mb: 2, 
          pageBreakInside: 'avoid', 
          borderRadius: 1,
          backgroundColor: (t) => t.palette.mode === 'dark' ? '#000' : '#fff',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 16px rgba(0,0,0,0.3)'
            : '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          {renderDayHeader(day)}
          <Divider sx={{ my: 1 }} />
          {renderColumnHeader()}
          <VirtualList
            items={day.strips}
            itemHeight={124}
            overscan={10}
            renderItem={(strip, idx) => (
              <SceneStripRow
                key={strip.sceneId || strip.id || `strip_${i}_${idx}`}
                strip={strip}
                idx={idx}
                minutesOverride={minutesOverride}
                catManualMap={catManualMap}
                specialEquipMap={specialEquipMap}
                renderSceneStrip={renderSceneStrip}
                renderBreakStrip={renderBreakStrip}
              />
            )}
          />
        </Paper>
      ))}

      {/* Inline editors are above per scene; no dialog needed */}
    </Box>
  );
}

export default ShootingScheduleTab;
